import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * ErrorState — shown when a fetch or action fails.
 *
 * Props:
 *   error     - Error|string — message or Error object
 *   onRetry   - callback; hides button if omitted
 *   title     - string, default "حدث خطأ"
 *   size      - 'sm' | 'md' (default 'md')
 */
const SIZE = {
  sm: { wrap: 'py-8 px-4', title: 'text-base', desc: 'text-xs', icon: 22 },
  md: { wrap: 'py-14 px-6', title: 'text-lg', desc: 'text-sm', icon: 28 },
};

const ErrorState = ({
  error = null,
  onRetry = null,
  title = 'حدث خطأ',
  size = 'md',
  className = '',
}) => {
  const s = SIZE[size] || SIZE.md;

  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string' && error.length > 0
      ? error
      : 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.';

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn('flex flex-col items-center justify-center text-center', s.wrap, className)}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-danger-50 dark:bg-danger-900/30 mb-4">
        <AlertTriangle
          size={s.icon}
          strokeWidth={1.75}
          className="text-danger-600 dark:text-danger-400"
          aria-hidden="true"
        />
      </div>

      <h3 className={cn('font-semibold text-neutral-800 dark:text-neutral-100', s.title)}>
        {title}
      </h3>

      <p className={cn('mt-1.5 text-neutral-500 dark:text-neutral-400 max-w-md leading-relaxed', s.desc)}>
        {message}
      </p>

      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          <RefreshCw size={15} />
          إعادة المحاولة
        </button>
      )}
    </div>
  );
};

export default ErrorState;
